/**
 * ObservationVector — assembles the per-timestep feature record x(t) (§6)
 * that a duration-aware event model (HSMM, future) will consume.
 *
 * Today the engine collects multi-modal evidence as scattered z-scores
 * (SceneDetector.SensorEvidence) and separate detector outputs. The patent
 * describes a single high-dimensional observation vector combining gaze/
 * target likelihoods, physical/paper features, object + occlusion state,
 * input/voice activity, relations and context — plus per-channel missingness.
 *
 * This module is that assembly. It is the explicit bridge between the
 * current detectors (stage 1) and the not-yet-built HSMM segmenter (stage 3):
 * once x(t) exists as a clean stream, swapping the deterministic state machine
 * for a learned segmenter becomes an input-compatible change.
 *
 * Pure logic. Additive — nothing downstream depends on it yet.
 */

import type { SensorEvidence } from './SceneDetector';
import type { StateId } from '../types';
import type { TrackedObject, InteractionState } from './ObjectTracker';
import type { PaperZone, PartialHomographyKind } from './Homography';
import type { GazeLikelihoodResult, TargetLikelihood } from './GazeTargetLikelihood';

/**
 * Context features (§6): task conditions that CONDITION interpretation — they
 * are never themselves an alert signal. Carrying them in x(t) makes the record
 * self-describing (an HSMM-faithful input) rather than passing examMode aside.
 */
export interface ObservationContext {
  examMode?: string;
  taskType?: string;
  permittedMaterials?: string[];
  /** e.g. 'none' | 'adhd' | 'asd' | 'anxiety' — conditions interpretation, never a diagnosis. */
  accessibilityProfile?: string;
  /** Whether a per-user baseline is available for relative interpretation. */
  hasBaseline?: boolean;
}

export interface ObservationVector {
  t: number;

  // ── sensor channels (existing z-scores) ──
  gazeZ: number;
  keyboardZ: number;
  voiceSDR: number;
  stylusZ: number;
  bodyZ: number;

  // ── gaze / target ──
  paperZone: PaperZone | null;
  homographyKind: PartialHomographyKind;
  homographyConfidence: number;
  /** Top target of the gaze likelihood distribution, if computed. */
  gazeTop?: TargetLikelihood;
  /** Full distribution P_i(t), if computed. */
  targetLikelihoods?: TargetLikelihood[];
  /** Gaze uncertainty (bits); higher = more diffuse. */
  gazeEntropy?: number;

  // ── objects / occlusion ──
  numObjects: number;
  numCoasting: number;
  phoneVisible: boolean;
  penVisible: boolean;
  bookVisible: boolean;
  handObjectInteraction: InteractionState;

  // ── §5.10 ray-sample paper ratios (along the gaze ray, not a single point) ──
  gazeRatios?: { inside: number; boundary: number; outside: number };
  // ── §5.3 gaze confidence triple ──
  gazeConfidence?: { ray: number; headPose: number; faceTrack: number };
  // ── §5.7 object-occlusion summary (from tracked-object memory) ──
  objectOcclusion: { maxScore: number; numPredicted: number };

  // ── relations ──
  numPersons: number;
  mutualGaze: boolean;

  // ── context (§6 context_features): conditions, not verdict signals ──
  context?: ObservationContext;

  // ── state (current deterministic label; becomes latent under HSMM) ──
  state?: StateId;

  // ── quality: per-channel missingness (true = missing/unreliable) ──
  missing: {
    gaze: boolean;
    keyboard: boolean;
    voice: boolean;
    stylus: boolean;
    body: boolean;
    paper: boolean;
    objects: boolean;
  };
}

export interface ObservationInputs {
  t: number;
  sensors: SensorEvidence;
  paperZone?: PaperZone | null;
  homographyKind?: PartialHomographyKind;
  homographyConfidence?: number;
  gaze?: GazeLikelihoodResult | null;
  objects?: TrackedObject[] | null;
  numPersons?: number;
  mutualGaze?: boolean;
  /** §5.10 ray-sample ratios, when the caller has a real ray + homography. */
  gazeRatios?: { inside: number; boundary: number; outside: number };
  /** §5.3 gaze confidence triple, when available. */
  gazeConfidence?: { ray: number; headPose: number; faceTrack: number };
  context?: ObservationContext;
  state?: StateId;
  /** Channels the caller knows are unavailable this frame. */
  missingChannels?: Partial<ObservationVector['missing']>;
}

function labelPresent(objects: TrackedObject[], re: RegExp): boolean {
  return objects.some((o) => o.state === 'active' && re.test(o.label.toLowerCase()));
}

/**
 * Dominant hand–object interaction across tracks. Prefers the "strongest"
 * interaction (held > occluded_by_hand > contact > none).
 */
function dominantInteraction(objects: TrackedObject[]): InteractionState {
  const rank: Record<InteractionState, number> = {
    none: 0,
    contact: 1,
    occluded_by_hand: 2,
    held: 3,
  };
  let best: InteractionState = 'none';
  for (const o of objects) if (rank[o.interaction] > rank[best]) best = o.interaction;
  return best;
}

/** Assemble x(t) from available detector outputs. Missing inputs → flagged, not fatal. */
export function buildObservationVector(inp: ObservationInputs): ObservationVector {
  const objs = inp.objects ?? [];
  const s = inp.sensors;
  const kind = inp.homographyKind ?? 'none';

  const missing = {
    gaze: false,
    keyboard: false,
    voice: false,
    stylus: false,
    body: false,
    paper: kind === 'none',
    objects: (inp.objects ?? null) === null,
    ...(inp.missingChannels ?? {}),
  };

  return {
    t: inp.t,
    gazeZ: s.gazeZ,
    keyboardZ: s.keyboardZ,
    voiceSDR: s.voiceSDR,
    stylusZ: s.stylusZ,
    bodyZ: s.bodyZ,

    paperZone: inp.paperZone ?? null,
    homographyKind: kind,
    homographyConfidence: inp.homographyConfidence ?? 0,
    gazeTop: inp.gaze?.top,
    targetLikelihoods: inp.gaze?.distribution,
    gazeEntropy: inp.gaze?.entropy,

    numObjects: objs.filter((o) => o.state === 'active').length,
    numCoasting: objs.filter((o) => o.state === 'coasting').length,
    phoneVisible: labelPresent(objs, /(phone|cell|smartphone)/),
    penVisible: labelPresent(objs, /(pen|pencil|stylus)/),
    bookVisible: labelPresent(objs, /(book|notebook|교재|책)/),
    handObjectInteraction: dominantInteraction(objs),

    gazeRatios: inp.gazeRatios,
    gazeConfidence: inp.gazeConfidence,
    objectOcclusion: {
      maxScore: objs.reduce((m, o) => Math.max(m, o.occlusionScore ?? 0), 0),
      numPredicted: objs.filter((o) => o.memoryState === 'fully_occluded_predicted').length,
    },

    numPersons: inp.numPersons ?? 1,
    mutualGaze: inp.mutualGaze ?? false,

    context: inp.context,
    state: inp.state,
    missing,
  };
}
